import type { IceMode, IceServerMessage, IceSnapshot, PlayerInfo } from '@halvard/protocol';
import { ice } from '@halvard/rules';
import { createStore } from 'zustand/vanilla';
import { openIceSocket, type ConnectionStatus, type IceSocketOptions } from '../../net/iceSocket';
import { createIceGameStore, type IceGameStoreApi, type IcePlayers } from './store';
import { t } from '../../i18n';

export type IceRole = 'host' | 'guest' | 'spectator';

/** Everything about a live match except the board, which lives in the game store. */
export interface OnlineMatchState {
  status: ConnectionStatus;
  matchId: string | null;
  /** Host only: the token for the invite link. */
  joinToken: string | null;
  me: PlayerInfo | null;
  role: IceRole | null;
  mode: IceMode;
  players: { host: PlayerInfo | null; guest: PlayerInfo | null };
  connected: { host: boolean; guest: boolean };
  firstBrand: ice.Seat;
  /** The game on the board: 0 or 1. */
  index: number;
  finished: { result: ice.IceMatchGame; record: string }[];
  outcome: ice.IceMatchOutcome | null;
  /** Between the two games: the second game, waiting until the player moves on. */
  upcoming: ice.IceState | null;
  /** Last refusal or problem, in words. */
  error: string | null;
}

export interface CreateRequest {
  mode: IceMode;
  side: 'brand' | 'rime' | 'random';
  lake: { kind: 'random' } | { kind: 'seed'; seed: number };
}

export interface OnlineMatchOptions extends Pick<
  IceSocketOptions,
  'url' | 'tokens' | 'open' | 'schedule'
> {
  /** Join this match… */
  matchId?: string;
  joinToken?: string;
  /** …or create one. */
  create?: CreateRequest;
  /** The match exists: its id and, for the host, the invite token. */
  onCreated?: (matchId: string, joinToken: string) => void;
}

/**
 * Who plays what on this screen: our side is `human`, the other is `remote`. Spectators, and a
 * host still waiting for a friend, can't move at all.
 */
export function playersFor(
  role: IceRole | null,
  firstBrand: ice.Seat,
  index: number,
  opponentHere = true,
): IcePlayers {
  if ((role !== 'host' && role !== 'guest') || !opponentHere) {
    return { brand: 'remote', rime: 'remote' };
  }
  const mine = ice.sideOf(firstBrand, index, role);
  return mine === 'brand' ? { brand: 'human', rime: 'remote' } : { brand: 'remote', rime: 'human' };
}

export function namesFor(
  players: OnlineMatchState['players'],
  firstBrand: ice.Seat,
  index: number,
): Partial<Record<ice.Side, string>> {
  const names: Partial<Record<ice.Side, string>> = {};
  for (const seat of ['host', 'guest'] as const) {
    const player = players[seat];
    if (player) names[ice.sideOf(firstBrand, index, seat)] = player.name;
  }
  return names;
}

/** A live match: the connection, the match state, and a game store the board can use. */
export function createOnlineMatch(options: OnlineMatchOptions) {
  const meta = createStore<OnlineMatchState>()(() => ({
    status: 'connecting',
    matchId: options.matchId ?? null,
    joinToken: null,
    me: null,
    role: null,
    mode: options.create?.mode ?? 'match',
    players: { host: null, guest: null },
    connected: { host: false, guest: false },
    firstBrand: 'host',
    index: 0,
    finished: [],
    outcome: null,
    upcoming: null,
    error: null,
  }));
  // The invite token is used once: after that the server knows us by our guest token.
  let joinToken = options.joinToken;
  let created = false;

  const game: IceGameStoreApi = createIceGameStore(
    0,
    0,
    { brand: 'remote', rime: 'remote' },
    {
      submit: (action) => {
        const { matchId, index } = meta.getState();
        if (!matchId) return;
        socket.send({
          type: 'match.action',
          matchId,
          game: index,
          seq: game.getState().game.moves.length,
          action,
        });
      },
    },
  );

  const refreshPlayers = () => {
    const m = meta.getState();
    const players = playersFor(m.role, m.firstBrand, m.index, m.players.guest !== null);
    game.setState({
      players,
      names: namesFor(m.players, m.firstBrand, m.index),
      // Face our own side once there's someone to play.
      view:
        players.rime === 'human'
          ? 'rime'
          : players.brand === 'human'
            ? 'brand'
            : game.getState().view,
    });
  };

  /** Asks for a fresh snapshot, e.g. after falling out of step with the server. */
  const resync = () => {
    const matchId = meta.getState().matchId;
    if (matchId) socket.send({ type: 'match.join', matchId });
  };

  const applySnapshot = (snapshot: IceSnapshot) => {
    meta.setState({
      matchId: snapshot.matchId,
      role: snapshot.role,
      mode: snapshot.mode,
      players: snapshot.players,
      connected: snapshot.connected,
      firstBrand: snapshot.firstBrand,
      index: snapshot.current.index,
      finished: snapshot.finished,
      outcome: snapshot.outcome,
      upcoming: null,
      error: null,
    });
    if (snapshot.role !== 'spectator') joinToken = undefined;
    const m = meta.getState();
    game
      .getState()
      .load(
        snapshot.current.state,
        playersFor(m.role, m.firstBrand, m.index, m.players.guest !== null),
        namesFor(m.players, m.firstBrand, m.index),
      );
  };

  const handle = (message: IceServerMessage) => {
    const m = meta.getState();
    switch (message.type) {
      case 'match.created':
        meta.setState({ matchId: message.matchId, joinToken: message.joinToken });
        options.onCreated?.(message.matchId, message.joinToken);
        return;
      case 'match.snapshot':
        applySnapshot(message);
        return;
      case 'match.update': {
        if (ice.iceStateHash(message.state) !== message.hash) {
          resync();
          return;
        }
        if (m.upcoming && message.game === 1) {
          // The second game moved on while this player is still reading the first one's result.
          meta.setState({ upcoming: message.state });
          return;
        }
        if (message.game !== m.index) return;
        const side = ice.sideOf(m.firstBrand, message.game, message.by);
        game.getState().receive({ state: message.state, events: message.events, side });
        return;
      }
      case 'match.resigned': {
        if (message.game !== m.index) return;
        const { winner, winReason } = message.state;
        const events: ice.IceEvent[] =
          winner && winReason ? [{ kind: 'over', winner, reason: winReason }] : [];
        const side = ice.sideOf(m.firstBrand, message.game, message.by);
        game.getState().receive({ state: message.state, events, side });
        return;
      }
      case 'match.game-over':
        meta.setState({
          finished: [...m.finished, { result: message.result, record: message.record }],
        });
        return;
      case 'match.next':
        meta.setState({ upcoming: message.state });
        return;
      case 'match.over':
        meta.setState({ outcome: message.outcome });
        return;
      case 'match.joined':
        meta.setState({ players: { ...m.players, guest: message.guest } });
        refreshPlayers();
        return;
      case 'player.connection':
        meta.setState({ connected: { ...m.connected, [message.seat]: message.connected } });
        return;
      case 'error': {
        const text = t().online.errors[message.code] ?? message.message;
        // A used or wrong invite: watch the match instead.
        if ((message.code === 'GAME_FULL' || message.code === 'BAD_JOIN_TOKEN') && joinToken) {
          joinToken = undefined;
          resync();
          meta.setState({ error: t().online.nowWatching(text) });
          return;
        }
        if (message.code === 'STALE_SEQ') resync();
        game.getState().rejected(text);
        meta.setState({ error: text });
        return;
      }
      case 'welcome':
      case 'pong':
        return;
    }
  };

  const socket = openIceSocket({
    url: options.url,
    tokens: options.tokens,
    ...(options.open ? { open: options.open } : {}),
    ...(options.schedule ? { schedule: options.schedule } : {}),
    onStatus: (status) => meta.setState({ status }),
    onMessage: handle,
    onReady: () => {
      const { matchId } = meta.getState();
      if (matchId) {
        socket.send({ type: 'match.join', matchId, ...(joinToken ? { joinToken } : {}) });
      } else if (options.create && !created) {
        created = true;
        socket.send({ type: 'match.create', ...options.create });
      }
    },
  });

  return {
    meta,
    game,
    /** Gives up the game, and the match. */
    resign: () => {
      const { matchId } = meta.getState();
      if (matchId) socket.send({ type: 'match.resign', matchId });
    },
    /** Leaves the first game's result and shows the second game. */
    continueToNext: () => {
      const { upcoming } = meta.getState();
      if (!upcoming) return;
      meta.setState({ index: 1, upcoming: null });
      const m = meta.getState();
      game
        .getState()
        .load(upcoming, playersFor(m.role, m.firstBrand, 1), namesFor(m.players, m.firstBrand, 1));
    },
    close: () => socket.close(),
  };
}

export type OnlineMatch = ReturnType<typeof createOnlineMatch>;
